import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { all } from 'country-codes-list';
import { useState, useEffect } from "react";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import { getAllClients, getLeads } from "../../api/services/hroneApi";
import { ErrorText } from "../../StyledCss/styledComponents";
import MultiInput from "../MultiInput";

const AddProjectPopup = ({
  onSubmitData,
  closeBtn,
  defaultValues,
  isEditMode,
  isClosedStatus,
  isCloneMode,
  isLoading,
  errorMsg
}) => {
  const { 
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues || {},
  });
  const countries = all();
  const currencies = [...new Set(countries.map(c => c.currencyCode))].filter(Boolean).sort();

  const [modules, setModules] = useState(defaultValues?.modules || []);
  const [newModules, setNewModules] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [startDateError, setStartDateError] = useState("");
  const [leads, setLeads] = useState([]);
  const currentStatus = watch("status");

  useEffect(() => {
    if (isCloneMode) {
      setValue("startDate", "");
      setValue("endDate", null);
    }
  },
  //eslint-disable-next-line
   [isCloneMode]);

  const fetchClients = async () => {
    try {
      const clientsResponse = await getAllClients();

      if (clientsResponse.ok) {
        const fetchedClientsData = await clientsResponse.json();
        setClientsData(fetchedClientsData);
      } else {
      }
    } catch (error) {
      toast.error("Failed to load clients");
    }
  }
    const fetchLeads = async () => {
    try {
      const response = await getLeads();
      if (response.status === 200) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) { }
  };
  useEffect(() => {
    fetchClients();
    fetchLeads();
  }, []);

  useEffect(() => {
    if (defaultValues?.clientId && clientsData.length > 0) {
      setValue("clientId", defaultValues.clientId);
    }
  }, [ defaultValues?.clientId, clientsData, setValue]);

  useEffect(() => {
    if (defaultValues?.leadEmail && leads.length > 0) {
      setValue("leadEmail", defaultValues.leadEmail);
    }
    // eslint-disable-next-line
  }, [leads]);


  const handleAddModule = (value) => {
    setNewModules([...newModules, value]);
  };

  const handleRemoveModule = (moduleToRemove, isNew = false) => {
    if (isNew) {
      setNewModules(newModules.filter((module) => module !== moduleToRemove));
    } else {
      setModules(modules.filter((module) => module.id !== moduleToRemove.id));
    }
  };

  const onSubmit = (data) => {
    const { ...projectData } = data;

    const submitData = { ...projectData };

    if (!isEditMode || (defaultValues?.status === "PENDING" && data.startDate !== defaultValues?.startDate)) {
      submitData.status = "ACTIVE";
    }

    if (isEditMode) {
      // Keep existing modules as objects [{}]
      submitData.modules = modules;
      // Send new modules as strings array if any
      if (newModules.length > 0) {
        submitData.new_modules = newModules;
      }
    } else {
      // For adding new project, send all modules as strings ['']
      submitData.modules = [
        ...modules.map((module) => module.name || module),
        ...newModules,
      ];
    }

    if (isEditMode) {
      submitData.clone = defaultValues?.clone ? true : false;
    }

    onSubmitData(submitData);
  };

  const isEndDateActive = (() => {
    if (!defaultValues?.endDate) return false;
    const now = new Date();
    const end = new Date(defaultValues.endDate);
    return end > now;
  })();

  const minStartDate = defaultValues?.status === "PENDING"
    ? new Date().toISOString().split('T')[0]
    : defaultValues?.endDate
    ? new Date(new Date(defaultValues.endDate).getTime() + 86400000).toISOString().split('T')[0]
    : undefined;

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    if (val && val < minStartDate) {
      setStartDateError(`Start date must be after project end date ${defaultValues.endDate}`);
    } else {
      setStartDateError("");
    }
    setValue("startDate", val, { shouldValidate: true });
  };

  return (
    <div className="popup-content">
      <div className="add-employee-form">
        <h5 style={{ marginBottom: "15px" }}>Project Details</h5>
        <div className="add-employee-form-container">
           {/*Client Name */}

            <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={errors.clientId && { borderLeft: "10px solid red" }}
                {...register("clientId", { required: true })}
              >
                <option value="">---Select Client---</option>
                {clientsData?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label">Client*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>
          {/*Project Name */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder="Enter Project Name"
                style={{
                  ...(errors.name && { borderLeft: "10px solid red" }),
                  ...((isCloneMode || defaultValues?.clone) && { backgroundColor: "#f5f5f5" }),
                }}
                type="text"
                disabled={isCloneMode || defaultValues?.clone}
                {...register("name", {
                  required: true,
                  setValueAs: (value) => value?.trim(),
                })}
              />
              <StyledLabel className="user-label">Name*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <MultiInput
            placeholder="Enter Module Name"
            label="Module Name"
            existingItems={modules}
            newItems={newModules}
            onAdd={handleAddModule}
            onRemove={handleRemoveModule}
            existingItemKey="name"
          />

          {/* Start Date */}
          <div title={isEndDateActive ? "Project start date cannot be modified while the  project end date is active." : ""}>
            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="date"
                  min={minStartDate}
                  disabled={isEndDateActive || currentStatus === "PAUSED" || (isClosedStatus && currentStatus === "CLOSED")}
                  style={(startDateError || errors.startDate) ? { borderLeft: "10px solid red" } : {}}
                  {...register("startDate", { required: true })}
                  onChange={handleStartDateChange}
                  max="9999-12-31"
              />
                <StyledLabel hasValue className="user-label">
                  Start Date*
                </StyledLabel>
              </StyledInputContainer>
            </StyledInputGroup>
            {startDateError && (
              <ErrorText className="mt-1">*{startDateError}</ErrorText>
            )}
          </div>

          {isEndDateActive && (
            <div>
              <StyledInputGroup>
                <StyledInputContainer>
                  <StyledInput
                    type="date"
                    disabled
                    {...register("endDate")}
                    max="9999-12-31"
                  />
                  <StyledLabel hasValue className="user-label">
                    End Date
                  </StyledLabel>
                </StyledInputContainer>
              </StyledInputGroup>
            </div>
          )}
          {isEditMode && (
            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect
                  style={errors.status && { borderLeft: "10px solid red" }}
                  {...register("status", { required: true })}
                >
                  <option value="">---Select Status---</option>
                  <option value="ACTIVE">Active</option>
                  {defaultValues?.status === "PENDING" && <option value="PENDING">Pending</option>}
                  {!isClosedStatus && <option value="PAUSED">Paused</option>}
                  {isClosedStatus && <option value="CLOSED">Closed</option>}
                </StyledSelect>
                <StyledLabel className="user-label">Status*</StyledLabel>
              </StyledSelectContainer>
            </StyledSelectGroup>
          )}

            <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={errors.leadEmail && { borderLeft: "10px solid red" }}
                {...register("leadEmail", { required: true })}
              >
                <option value="">---Select Project Lead---</option>
                {leads?.map((lead) => (
                  <option key={lead} value={lead}>
                    {lead}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label">Project Lead*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>
        </div>

        <h5 style={{ marginBottom: "20px", marginTop: "18px" }}>
          Billing Details
        </h5>
        <div className="add-employee-form-container">
          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={
                  errors.billingCurrency && { borderLeft: "10px solid red" }
                }
                {...register("billingCurrency", { required: true })}
              >
                <option value="">--- Select Billing Currency ---</option>
                {currencies.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label">Currency*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>
        </div>
        {errorMsg && (
          <ErrorText className="mt-3">*{errorMsg}</ErrorText>
        )}

        {/* Buttons */}
        <div className="d-flex gap-2 justify-content-end mt-1">
          <SecondaryButton onClick={closeBtn}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit(onSubmit)} disabled={isLoading || startDateError}>
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Adding..."
              : isEditMode
              ? "Update"
              : "Add"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default AddProjectPopup;
